/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { db } from '../../firebase';
import Breadcrumbs from '../components/Breadcrumbs';
import '../pages/admin.css';

function fmt(val) {
  if (!val) return 'Never';
  if (val._seconds) return new Date(val._seconds * 1000).toLocaleString();
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleString();
}

const ProjectParticipants = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { loading } = useAuthContext();
  const { addAlert } = useAlert();

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 15;

  // Fetch participants
  const fetchAllParticipants = useCallback(async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, 'participants'), where('projectId', '==', projectId));
      const snap = await getDocs(q);
      setParticipants(snap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() })));
    } catch (err) {
      addAlert('error', 'Failed to fetch participants');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, addAlert]);

  useEffect(() => {
    if (projectId) fetchAllParticipants();
  }, [projectId, fetchAllParticipants]);

  // Subscribe to project
  useEffect(() => {
    if (!projectId) { setIsLoading(false); return; }
    const unsub = onSnapshot(doc(db, 'projects', projectId), (snap) => {
      if (snap.exists()) setProject(snap.data());
      else addAlert('error', `Project ${projectId} not found`);
      setIsLoading(false);
    }, (err) => {
      addAlert('error', err.message);
      setIsLoading(false);
    });
    return () => unsub();
  }, [projectId, addAlert]);

  const filtered = participants.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.id?.toLowerCase().includes(q) ||
      p.registration?.prolific?.pId?.toLowerCase().includes(q) ||
      p.fitbitData?.user_id?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const completedCount = participants.filter((p) => p.completed).length;

  return (
    <div className="ja-root">
      <div className="ja-page">

        {/* Header */}
        <div className="ja-page-header">
          <div>
            <Breadcrumbs items={[{ label: 'Dashboard', to: '/admin' },
              { label: project?.name || 'Project', to: `/admin/showProject/${projectId}` }, { label: 'Participants' }]} />
            <h1 className="ja-page-title"><strong>{project?.name || '…'}</strong></h1>
            <p className="ja-page-subtitle">All registered participants for this project.</p>
          </div>
          <button className="ja-btn ja-btn-ghost" onClick={() => navigate(-1)}>← Back to project</button>
        </div>

        {(isLoading || loading) && <div className="ja-progress"><div className="ja-progress-bar" /></div>}

        {/* Stats row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))',
          gap: 1, border: '1px solid var(--jp-gray-200)', borderRadius: 'var(--jp-radius)',
          overflow: 'hidden', background: 'var(--jp-gray-200)', marginBottom: 24,
        }}>
          {[
            { num: participants.length, label: 'Total registered' },
            { num: completedCount, label: 'Completed' },
            { num: participants.length - completedCount, label: 'Incomplete' },
          ].map((s) => (
            <div key={s.label} style={{ background: '#fff', padding: '16px 18px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--jp-mono)', fontSize: 22, fontWeight: 500, color: 'var(--jp-blue-mid)', lineHeight: 1.1, marginBottom: 4 }}>{s.num}</div>
              <div style={{ fontSize: 12, color: 'var(--jp-gray-600)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search + refresh */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            className="ja-input" style={{ maxWidth: 320 }}
            placeholder="Search by ID, Prolific ID, Fitbit ID…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
          <button className="ja-btn ja-btn-ghost" onClick={fetchAllParticipants} disabled={isLoading}>↻ Refresh</button>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--jp-gray-600)', fontFamily: 'var(--jp-mono)', alignSelf: 'center' }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', border: '1px solid var(--jp-gray-200)', borderRadius: 'var(--jp-radius)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--jp-gray-50)', borderBottom: '1px solid var(--jp-gray-200)' }}>
                {['Participant ID', 'Prolific ID', 'Started', 'Step', 'Completed', 'Last update', 'Fitbit ID', 'Fitbit req.', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--jp-gray-600)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr><td colSpan={9} style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--jp-gray-600)', fontSize: 13 }}>
                  {isLoading ? 'Loading…' : 'No participants found.'}
                </td></tr>
              )}
              {paged.map((p, i) => (
                <tr key={p.id} style={{ borderBottom: i < paged.length - 1 ? '1px solid var(--jp-gray-100)' : 'none', transition: 'background 80ms' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--jp-gray-50)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--jp-mono)', fontSize: 11, verticalAlign: 'middle' }}>{p.id}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--jp-mono)', fontSize: 11, verticalAlign: 'middle' }}>{p.registration?.prolific?.pId || <span style={{ color: 'var(--jp-gray-400)' }}>N/A</span>}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{fmt(p.registrationStartedAt).split(',')[0]}</td>
                  <td style={{ padding: '10px 14px', verticalAlign: 'middle', textAlign: 'center' }}>
                    <span className="ja-chip" style={{ background: 'var(--jp-gray-100)', color: 'var(--jp-gray-600)' }}>{p.registrationStep ?? '–'}</span>
                  </td>
                  <td style={{ padding: '10px 14px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {p.completed
                      ? <span style={{ color: '#136C13', fontWeight: 600 }}>✓</span>
                      : <span style={{ color: '#C0392B', fontWeight: 600 }}>✗</span>}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{fmt(p.updatedAt)}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--jp-mono)', fontSize: 11, verticalAlign: 'middle' }}>{p.fitbitData?.user_id || <span style={{ color: 'var(--jp-gray-400)' }}>N/A</span>}</td>
                  <td style={{ padding: '10px 14px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {p.fitbitRequest?.participantId
                      ? <span style={{ color: '#136C13', fontWeight: 600 }}>✓</span>
                      : <span style={{ color: '#C0392B', fontWeight: 600 }}>✗</span>}
                  </td>
                  <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>
                    <button className="ja-btn ja-btn-ghost"
                      onClick={() => navigate(`/admin/participants/${p.id}/project/${projectId}`)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <button className="ja-btn ja-btn-ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
            <span style={{ fontSize: 12, color: 'var(--jp-gray-600)', fontFamily: 'var(--jp-mono)' }}>{page + 1} / {totalPages}</span>
            <button className="ja-btn ja-btn-ghost" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProjectParticipants;
